import TextCounterBlockProps from "./TextCounterProps.interface";
import styles from "./TextCounter.module.scss";

import { useInView } from "react-intersection-observer";
import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import CountUp from "react-countup";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import Paragraph from "components/Paragraph/Paragraph";
import React, { FunctionComponent } from "react";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import useAnchor from "blocks/hooks/useAnchor";

const TextCounter: FunctionComponent<TextCounterBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { ref, inView } = useInView({ triggerOnce: true });

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);

    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;
    const counterPositionClass = props.settings.counterPosition ? styles[`position_${props.settings.counterPosition}`] : undefined;

    return (
        <section className={combineClasses(styles.wrapper, counterPositionClass, inViewClasses, ...splitClasses, ...wrapperClasses)} style={{ ...wrapperStyles, ...splitStyles }} id={identifier}>
            <div className={styles.container} ref={inViewElementRef}>
                <div className={styles.unit_number} ref={ref}>
                    {inView ? (
                        <CountUp duration={2} separator="," start={(props.content.amount) / 2} end={props.content.amount} />
                    ) : null}
                    <span className={styles.unit_indicator}>{props.content.unit}</span>
                </div>

                <div className={styles.content}>
                    {/* Tagline */}
                    {props.content.tagline ? (
                        <span className={styles.tagline}>{props.content.tagline}</span>
                    ) : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading key={`head_${props.contentUdi}`} className={combineClasses(styles.headline, headlineCaseClass)} text={props.content.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight} />
                    ) : null}

                    {/* Text */}
                    {props.content.bodyText ? (
                        <Paragraph key={`para_${props.contentUdi}`} className={styles.text} innerHTML={props.content.bodyText} />
                    ) : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons.length > 0 ? (
                        <div className={styles.buttons}>
                            {props.content.buttons.map((button) => {
                                return (
                                    <Button key={button.contentUdi} to={button.content.link} className={styles.button} variant={button.content.variant} {...button.content}>{button.content.label}</Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={props.contentUdi} {...props.settings.backgroundMedia[0]} />
            ) : null}
        </section>
    );
};

export default TextCounter;